/**
 * Deliberately NOT `as const`: that would freeze every value to its literal
 * string type, and the Sinhala bundle could then never satisfy the schema.
 * The nested key structure is still captured, which is what gives `t()` its
 * typed key paths.
 */
export const en = {
  app: {
    name: 'Tharanga Steel',
    subtitle: 'Collection System',
  },

  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    remove: 'Remove',
    search: 'Search',
    filter: 'Filter',
    clear: 'Clear',
    apply: 'Apply',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    confirm: 'Confirm',
    print: 'Print',
    download: 'Download',
    export: 'Export',
    import: 'Import',
    refresh: 'Refresh',
    loading: 'Loading…',
    saving: 'Saving…',
    noResults: 'No results',
    none: 'None',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    active: 'Active',
    inactive: 'Inactive',
    status: 'Status',
    actions: 'Actions',
    date: 'Date',
    from: 'From',
    to: 'To',
    today: 'Today',
    total: 'Total',
    optional: 'optional',
    required: 'required',
    selectPlaceholder: 'Select…',
    searchPlaceholder: 'Type to search…',
    unassigned: 'Unassigned',
    showing: 'Showing {{from}}–{{to}} of {{total}}',
    page: 'Page {{page}} of {{pages}}',
    retry: 'Try again',
    somethingWentWrong: 'Something went wrong',
    notFound: 'Not found',
  },

  nav: {
    dashboard: 'Dashboard',
    collect: 'Collect',
    myCollections: 'My Collections',
    collections: 'Collections',
    invoices: 'Invoices',
    shops: 'Customers',
    agents: 'Agents',
    routes: 'Routes',
    reports: 'Reports',
    users: 'Users',
    auditLog: 'Audit Log',
    banks: 'Banks',
    settings: 'Settings',
    masterData: 'Master Data',
    logout: 'Sign out',
    language: 'Language',
  },

  auth: {
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    username: 'Username',
    password: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    changePassword: 'Change password',
    changePasswordTitle: 'Choose a new password',
    mustChangePassword:
      'Your password was set by an administrator. Choose a new one before continuing.',
    passwordChanged: 'Password updated. Please sign in again.',
    welcome: 'Sign in to continue',
    invalidCredentials: 'Incorrect username or password',
    accountDisabled: 'This account has been deactivated',
    sessionExpired: 'Your session has expired. Please sign in again.',
    passwordsDoNotMatch: 'The passwords do not match',
    passwordRules: 'At least 8 characters, including a letter and a number',
    signedInAs: 'Signed in as',
  },

  dashboard: {
    title: 'Dashboard',
    totalInvoiced: 'Total invoiced',
    totalCollected: 'Total collected',
    totalOutstanding: 'Outstanding',
    collectionRate: 'Collection rate',
    pendingInvoices: 'Pending invoices',
    overdueInvoices: 'Overdue invoices',
    overdueAmount: 'Overdue amount',
    dailyCollection: 'Daily collection',
    agentCollection: 'Collection by agent',
    topByPending: 'Top customers by outstanding',
    topByInvoiced: 'Top customers by invoiced value',
    overdueAgeing: 'Overdue ageing',
    routeView: 'Routes',
    worstOverdue: 'Longest overdue',
    collectedToday: 'Collected today',
    shops: 'Customers',
    daysOverdue: '{{count}} days overdue',
    bucket: 'Days overdue',
    noCollections: 'No collections in this period',
    last30Days: 'Last 30 days',
    last7Days: 'Last 7 days',
    thisMonth: 'This month',
  },

  shop: {
    title: 'Customers',
    one: 'Customer',
    new: 'New customer',
    edit: 'Edit customer',
    clientName: 'Client name',
    code: 'Code',
    contactPerson: 'Contact person',
    contactNumber: 'Contact number',
    address: 'Address',
    addressLine: 'Address line {{n}}',
    route: 'Route',
    agent: 'Agent',
    outstanding: 'Outstanding',
    openInvoices: 'Open invoices',
    noRoute: 'Not on a route',
    unassignedOnly: 'Not on any route',
    withOutstandingOnly: 'With outstanding balance only',
    created: 'Customer created',
    updated: 'Customer updated',
    empty: 'No customers yet. Add the first one to get started.',
    noRouteWarning:
      'This customer is not on a route, so no agent will see its invoices. Assign a route.',
    invoiceHistory: 'Invoice history',
  },

  agent: {
    title: 'Agents',
    one: 'Agent',
    new: 'New agent',
    edit: 'Edit agent',
    name: 'Agent name',
    code: 'Code',
    contactNumber: 'Contact number',
    nic: 'NIC',
    address: 'Address',
    routes: 'Routes',
    login: 'Mobile login',
    hasLogin: 'Has login',
    noLogin: 'No login',
    createLogin: 'Create login',
    resetLogin: 'Reset login',
    loginHelp:
      'Optional. Creates the credential this agent signs in with. They will be asked to change the password at first sign-in.',
    loginSet: 'Login saved. The agent must change the password at first sign-in.',
    created: 'Agent created',
    updated: 'Agent updated',
    empty: 'No agents yet.',
    deactivateWarning:
      'Deactivating an agent also disables their login and ends their sessions.',
  },

  route: {
    title: 'Routes',
    one: 'Route',
    new: 'New route',
    edit: 'Edit route',
    name: 'Route name',
    namePlaceholder: 'e.g. Mahawewa to Divulapitiya',
    description: 'Description',
    agent: 'Assigned agent',
    shopCount: 'Customers',
    assignAgent: 'Assign agent',
    assignShops: 'Add customers',
    removeShop: 'Remove from route',
    noAgent: 'No agent assigned',
    created: 'Route created',
    updated: 'Route updated',
    agentAssigned: 'Agent assigned. Outstanding invoices on this route now go to them.',
    shopsAssigned: '{{count}} customers added to this route',
    shopsMoved: '{{count}} of them were moved from another route',
    shopRemoved: 'Customer removed from this route',
    empty: 'No routes yet.',
    mappingHelp:
      'A customer belongs to exactly one route, and a route has one agent. Invoices follow this chain automatically.',
  },

  invoice: {
    title: 'Invoices',
    one: 'Invoice',
    new: 'New invoice',
    edit: 'Edit invoice',
    invoiceNo: 'Invoice number',
    customer: 'Customer',
    description: 'Description',
    amount: 'Invoice amount',
    invoiceDate: 'Invoice date',
    dueDate: 'Due date',
    outstanding: 'Outstanding',
    collected: 'Collected',
    overdue: 'Overdue',
    overdueOnly: 'Overdue only',
    outstandingOnly: 'Outstanding only',
    created: 'Invoice created',
    updated: 'Invoice updated',
    cancelled: 'Invoice cancelled',
    cancelTitle: 'Cancel this invoice',
    cancelReason: 'Reason',
    cancelWarning: 'This cannot be undone. The invoice will no longer be collectable.',
    editFloorNotice: "{{amount}} already collected against this invoice — the amount can't be reduced below that.",
    editReasonLabel: 'Reason for change',
    editReasonHint: 'Required because this invoice already has collections against it.',
    moveReasonHint: 'Required when moving an invoice to another customer.',
    moveShopNotice:
      'This moves the invoice to another customer and their agent. Agents see the change the next time their app syncs.',
    routedTo: 'Routed to',
    collectionHistory: 'Collections against this invoice',
    empty: 'No invoices yet.',
    status: {
      OPEN: 'Open',
      PARTIALLY_PAID: 'Part paid',
      PAID: 'Paid',
      CANCELLED: 'Cancelled',
    },
    import: {
      title: 'Import invoices',
      selectFile: 'Choose a CSV file',
      dryRun: 'Validate only, do not import',
      allowPartial: 'Import valid rows and skip the invalid ones',
      help: 'Columns: invoice_no, shop_code (or shop_name), description, amount, invoice_date, due_date',
      validating: 'Validating…',
      importing: 'Importing…',
      resultTitle: 'Import result',
      totalRows: 'Rows read',
      validRows: 'Valid',
      imported: 'Imported',
      skipped: 'Skipped',
      rowErrors: 'Row errors',
      row: 'Row {{row}}',
      allOrNothing:
        'By default nothing is imported if any row is invalid. Fix the file, or tick the option above.',
      success: '{{count}} invoices imported',
      dryRunSuccess: 'The file is valid. {{count}} invoices are ready to import.',
    },
  },

  collection: {
    title: 'Collections',
    one: 'Collection',
    new: 'Record a collection',
    receiptNo: 'Receipt no',
    customer: 'Customer',
    agent: 'Agent',
    collectedAt: 'Collected at',
    collectionDate: 'Collection date',
    cash: 'Cash',
    cheque: 'Cheque',
    chequeNo: 'Cheque no',
    chequeDate: 'Cheque date',
    bank: 'Bank',
    amount: 'Amount',
    method: 'Method',
    notes: 'Notes',
    payments: 'Money collected',
    addCash: 'Add cash',
    addCheque: 'Add cheque',
    allocations: 'Assign to invoices',
    autoAllocate: 'Assign oldest first',
    clearAllocations: 'Clear',
    collected: 'Collected',
    assigned: 'Assigned',
    remaining: 'Left to assign',
    overAssigned: 'Over-assigned',
    balanced: 'Balanced',
    issueReceipt: 'Issue receipt',
    issuing: 'Issuing…',
    issued: 'Receipt {{receiptNo}} issued',
    selectCustomer: 'Select a customer',
    selectCustomerFirst: 'Choose a customer to see their pending invoices',
    noPendingInvoices: 'This customer has no pending invoices.',
    pendingInvoices: 'Pending invoices',
    payFull: 'Pay in full',
    slip: 'Customer slip',
    printSlip: 'Print slip',
    newCollection: 'Record another',
    void: 'Void receipt',
    voidTitle: 'Void this receipt',
    voidReason: 'Reason',
    voidWarning:
      'This reverses every amount assigned by this receipt and reopens those invoices. The receipt is kept and marked void.',
    voided: 'Receipt voided',
    voidedBadge: 'Void',
    empty: 'No collections recorded yet.',
    duplicateSubmission: 'This collection was already recorded — showing the original receipt.',
    issues: {
      NO_PAYMENTS: 'Record at least one cash or cheque payment',
      INVALID_PAYMENT_AMOUNT: 'Enter an amount greater than zero',
      CHEQUE_DETAILS_REQUIRED: 'A cheque needs its number, date and bank',
      DUPLICATE_CHEQUE: 'This cheque has already been entered on this receipt',
      NO_ALLOCATIONS: 'Assign the collected money to at least one invoice',
      ALLOCATION_EXCEEDS_OUTSTANDING:
        'Invoice {{invoiceNo}}: cannot assign more than is outstanding',
      UNDER_ALLOCATED: 'LKR {{amount}} of the collected money is still unassigned',
      OVER_ALLOCATED: 'You have assigned LKR {{amount}} more than was collected',
    },
  },

  report: {
    title: 'Reports',
    agentDaily: 'Agent daily detail',
    agentDailyDesc: 'The full collection sheet for one agent on one day',
    agentTotals: 'Agent totals',
    agentTotalsDesc: 'What each agent collected on a day',
    shopBreakdown: 'Customer breakdown',
    shopBreakdownDesc: 'Daily collections by customer',
    summary: 'Collection summary',
    summaryDesc: 'Daily totals across a date range',
    routeDaily: 'Route collections',
    routeDailyDesc: 'A day’s collections grouped by route',
    cheques: 'Cheque register',
    chequesDesc: 'Every cheque taken in a date range',
    selectAgent: 'Agent',
    selectDate: 'Date',
    downloadPdf: 'Download PDF',
    printReport: 'Print',
    generating: 'Generating…',
    noData: 'No collections for this selection.',
    sheet: {
      heading: 'COLLECTION SUMMARY REPORTS',
      date: 'Date',
      estName: 'M.Est. Name',
      serialNo: 'Serial No',
      receiptNo: 'Receipt No',
      customerName: 'Customer Name',
      invoiceNo: 'Invoice No.',
      invoiceDate: 'Invoice Date',
      createdDate: 'Created Date',
      invoiceAmount: 'Invoice Amount',
      cash: 'Cash',
      cheque: 'Cheque',
      chequeNo: 'Cheque No.',
      chequeDate: 'Cheque Date',
      bank: 'Bank',
      total: 'Total',
      collectedBy: 'Collected by (Agent)',
      checkedBy: 'Checked by',
      approvedBy: 'Approved by',
    },
    columns: {
      receipts: 'Receipts',
      customers: 'Customers',
      cash: 'Cash',
      cheque: 'Cheque',
      total: 'Total',
    },
  },

  user: {
    title: 'Users',
    one: 'User',
    new: 'New user',
    edit: 'Edit user',
    username: 'Username',
    fullName: 'Full name',
    role: 'Role',
    lastLogin: 'Last sign-in',
    resetPassword: 'Reset password',
    passwordReset: 'Password reset. The user must change it at next sign-in.',
    created: 'User created',
    updated: 'User updated',
    agentLoginNote: 'Agent logins are created on the agent record, not here.',
    roles: {
      ADMIN: 'Administrator',
      FINANCE: 'Finance',
      AGENT: 'Agent',
    },
  },

  auditLog: {
    title: 'Audit Log',
    description: 'Who did what, and when — invoice creation, invoice edits, and report downloads.',
    dateTime: 'Date & Time',
    user: 'User',
    action: 'Action',
    details: 'Details',
    allActions: 'All actions',
    allUsers: 'All users',
    system: 'System',
    actions: {
      invoiceCreated: 'Invoice Created',
      invoiceUpdated: 'Invoice Edited',
      reportDownloaded: 'Report Downloaded',
    },
  },

  bank: {
    title: 'Banks',
    one: 'Bank',
    new: 'New bank',
    name: 'Bank name',
    shortCode: 'Short code',
    shortCodeHelp: 'Printed in the Bank column of the report, e.g. BOC, P/B',
    created: 'Bank added',
    updated: 'Bank updated',
  },

  errors: {
    network: 'Cannot reach the server. Check your connection.',
    unauthorized: 'Please sign in again.',
    forbidden: 'You do not have permission to do that.',
    notFound: 'That record no longer exists.',
    conflict: 'That conflicts with existing data.',
    validation: 'Please check the highlighted fields.',
    server: 'The server had a problem. Try again in a moment.',
    unknown: 'Something went wrong.',
    pageNotFound: 'That page does not exist.',
    goHome: 'Go to dashboard',
  },
};

export type TranslationSchema = typeof en;
